import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Mail, Trash2, Tag, Send, Users, MessageSquareHeart, Star, Bell, Eye, Zap } from "lucide-react";

function NudgesPanel() {
  const [data, setData] = useState(null);
  const [abStats, setAbStats] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/nudges/preview").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/nudges/ab-stats").then((r) => setAbStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/nudges/run");
      toast.success(`Sweep complete — ${r.data.sent} sent, ${r.data.skipped} skipped`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to run sweep");
    } finally {
      setRunning(false);
    }
  };

  if (!data) return <div className="text-muted_ink">Loading…</div>;

  return (
    <div data-testid="admin-nudges" className="space-y-6">
      <div className="border border-edge bg-linen p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center flex-shrink-0">
            <Bell className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-serif text-2xl">Abandoned-preview nudges</div>
            <p className="text-sm text-muted_ink mt-1 max-w-2xl">
              When a signed-in reader previews a book but doesn't buy within {Math.round(data.delay_hours/24)} days,
              we send them a friendly nudge with code <span className="font-mono">{data.code}</span> ({data.discount_percent}% off).
              The sweep runs automatically every hour.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-edge bg-white p-6">
          <div className="label-eyebrow flex items-center gap-2"><Eye className="w-3.5 h-3.5" /> Total previews</div>
          <div className="font-serif text-4xl mt-2">{data.total_previews}</div>
        </div>
        <div className="border border-edge bg-white p-6">
          <div className="label-eyebrow flex items-center gap-2"><Bell className="w-3.5 h-3.5" /> Nudges sent</div>
          <div className="font-serif text-4xl mt-2">{data.total_nudged}</div>
        </div>
        <div className="border border-terracotta bg-linen p-6">
          <div className="label-eyebrow flex items-center gap-2"><Zap className="w-3.5 h-3.5" /> Pending sweep</div>
          <div className="font-serif text-4xl mt-2">{data.pending_count}</div>
        </div>
      </div>

      <div>
        <button
          data-testid="run-nudges-btn"
          onClick={runNow}
          disabled={running}
          className="btn-primary disabled:opacity-60"
        >
          <Zap className="w-4 h-4 mr-2" />
          {running ? "Running…" : "Run sweep now"}
        </button>
      </div>

      {data.pending.length > 0 && (
        <div className="border border-edge bg-white">
          <div className="grid grid-cols-12 px-4 py-3 label-eyebrow border-b border-edge">
            <div className="col-span-6">Reader</div>
            <div className="col-span-3">Book</div>
            <div className="col-span-3 text-right">Previewed</div>
          </div>
          {data.pending.map((p) => (
            <div key={`${p.user_email}:${p.book_id}`} className="grid grid-cols-12 px-4 py-3 border-b border-edge text-sm">
              <div className="col-span-6 truncate">{p.user_email}</div>
              <div className="col-span-3 font-mono text-xs text-muted_ink truncate">{p.book_id.slice(-8)}</div>
              <div className="col-span-3 text-right text-muted_ink">{new Date(p.previewed_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      )}

      {/* A/B copy test */}
      {abStats && (
        <div data-testid="nudge-ab-stats" className="border border-edge bg-white p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="label-eyebrow flex items-center gap-2">
                <Star className="w-3.5 h-3.5" /> A/B copy test
              </div>
              <p className="text-sm text-muted_ink mt-1 max-w-xl">
                Each nudge is randomly assigned copy variant A ("Still thinking about it?") or B ("The book you started is still here.").
              </p>
            </div>
            {abStats.winner && abStats.winner !== "tie" && (
              <div className="text-sm">
                <span className="label-eyebrow text-terracotta">Winning:</span> Variant {abStats.winner}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["A", "B"].map((v) => {
              const s = abStats.variants[v];
              const isWinner = abStats.winner === v;
              return (
                <div key={v} data-testid={`ab-variant-${v}`}
                     className={`border p-5 ${isWinner ? "border-terracotta bg-linen" : "border-edge"}`}>
                  <div className="flex items-baseline justify-between">
                    <div className="font-serif text-3xl">Variant {v}</div>
                    <div className="label-eyebrow">{v === "A" ? "warm" : "patient"}</div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="font-serif text-2xl">{s.sent}</div>
                      <div className="text-xs text-muted_ink">sent</div>
                    </div>
                    <div>
                      <div className="font-serif text-2xl">{s.conversions}</div>
                      <div className="text-xs text-muted_ink">bought</div>
                    </div>
                    <div>
                      <div className="font-serif text-2xl text-terracotta">{s.conversion_rate}%</div>
                      <div className="text-xs text-muted_ink">rate</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function FeedbackPanel() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, avg_rating: 0 });

  const load = useCallback(() => {
    Promise.all([
      api.get("/admin/feedback"),
      api.get("/admin/feedback/stats"),
    ]).then(([r, s]) => { setRows(r.data); setStats(s.data); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-feedback" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-edge bg-white p-6">
          <div className="label-eyebrow">Total feedback</div>
          <div className="font-serif text-4xl mt-2">{stats.total}</div>
        </div>
        <div className="border border-edge bg-white p-6">
          <div className="label-eyebrow">Average rating</div>
          <div className="font-serif text-4xl mt-2 flex items-center gap-2">
            {stats.avg_rating || 0}
            <Star className="w-6 h-6 fill-terracotta text-terracotta" strokeWidth={1} />
          </div>
        </div>
      </div>

      <div className="border border-edge bg-white">
        <div className="grid grid-cols-12 px-4 py-3 label-eyebrow border-b border-edge">
          <div className="col-span-2">Rating</div>
          <div className="col-span-6">Comment</div>
          <div className="col-span-2">Email / Page</div>
          <div className="col-span-2 text-right">When</div>
        </div>
        {rows.length === 0 && <div className="p-6 text-muted_ink">No feedback yet — the "How's Folio?" tab is live on every page.</div>}
        {rows.map((r) => (
          <div key={r.id} className="grid grid-cols-12 px-4 py-4 border-b border-edge text-sm items-start">
            <div className="col-span-2 flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-terracotta text-terracotta" : "text-edge"}`} strokeWidth={1} />
              ))}
            </div>
            <div className="col-span-6 text-ink whitespace-pre-wrap break-words pr-3">
              {r.comment || <span className="italic text-muted_ink">— no comment —</span>}
            </div>
            <div className="col-span-2 text-xs text-muted_ink break-all">
              <div>{r.email || "anonymous"}</div>
              {r.page && <div className="opacity-60">{r.page}</div>}
            </div>
            <div className="col-span-2 text-right text-xs text-muted_ink">
              {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubscribersPanel() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/subscribers").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    await api.delete(`/admin/subscribers/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div data-testid="admin-subscribers" className="border border-edge bg-white">
      <div className="grid grid-cols-12 px-4 py-3 label-eyebrow border-b border-edge">
        <div className="col-span-4">Email</div>
        <div className="col-span-2">Name</div>
        <div className="col-span-4">Favorite categories</div>
        <div className="col-span-1">Status</div>
        <div className="col-span-1 text-right">Actions</div>
      </div>
      {rows.length === 0 && <div className="p-6 text-muted_ink">No subscribers yet.</div>}
      {rows.map((r) => (
        <div key={r.id} className="grid grid-cols-12 px-4 py-3 border-b border-edge text-sm items-center">
          <div className="col-span-4 truncate">{r.email}</div>
          <div className="col-span-2 truncate">{r.name || "—"}</div>
          <div className="col-span-4 text-xs text-muted_ink truncate">{(r.favorite_categories || []).join(", ") || "—"}</div>
          <div className="col-span-1">
            <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-widest ${
              r.unsubscribed ? "bg-terracotta/10 text-terracotta" : "bg-sage/20 text-sage"
            }`}>{r.unsubscribed ? "Off" : "Active"}</span>
          </div>
          <div className="col-span-1 text-right">
            <button onClick={() => del(r.id)} data-testid={`sub-delete-${r.id}`} className="p-2 hover:text-terracotta">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function DiscountCodesPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", percent: "10", max_uses: "0", expires_at: "", active: true });

  const load = useCallback(() => {
    api.get("/admin/discount-codes").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/discount-codes", {
        code: form.code.trim().toUpperCase(),
        percent: parseInt(form.percent, 10),
        max_uses: parseInt(form.max_uses || "0", 10),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        active: form.active,
      });
      toast.success("Code created");
      setForm({ code: "", percent: "10", max_uses: "0", expires_at: "", active: true });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete code?")) return;
    await api.delete(`/admin/discount-codes/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-codes" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={submit} className="lg:col-span-1 bg-white border border-edge p-6 space-y-4">
        <div className="flex items-center gap-2 font-serif text-2xl"><Tag className="w-5 h-5 text-terracotta" />New code</div>

        <div><label className="label-eyebrow block mb-1">Code</label>
          <Input data-testid="code-input" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-none border-edge font-mono" placeholder="READMORE10" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-eyebrow block mb-1">Percent off</label>
            <Input required type="number" min="1" max="99" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} className="rounded-none border-edge" /></div>
          <div><label className="label-eyebrow block mb-1">Max uses (0=∞)</label>
            <Input type="number" min="0" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="rounded-none border-edge" /></div>
        </div>
        <div><label className="label-eyebrow block mb-1">Expires (optional)</label>
          <Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="rounded-none border-edge" /></div>
        <button data-testid="code-save-btn" type="submit" className="btn-primary w-full">Create code</button>
      </form>

      <div className="lg:col-span-2">
        <div className="border border-edge bg-white">
          <div className="grid grid-cols-12 px-4 py-3 label-eyebrow border-b border-edge">
            <div className="col-span-3">Code</div>
            <div className="col-span-1">%</div>
            <div className="col-span-2">Uses</div>
            <div className="col-span-3">Expires</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Del</div>
          </div>
          {rows.length === 0 && <div className="p-6 text-muted_ink">No codes yet.</div>}
          {rows.map((r) => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-3 border-b border-edge text-sm items-center">
              <div className="col-span-3 font-mono">{r.code}</div>
              <div className="col-span-1">{r.percent}%</div>
              <div className="col-span-2">{r.uses_count}/{r.max_uses || "∞"}</div>
              <div className="col-span-3 text-xs text-muted_ink">
                {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"}
              </div>
              <div className="col-span-2">
                <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-widest ${
                  r.active ? "bg-sage/20 text-sage" : "bg-linen text-muted_ink"
                }`}>{r.active ? "Active" : "Off"}</span>
              </div>
              <div className="col-span-1 text-right">
                <button onClick={() => del(r.id)} data-testid={`code-delete-${r.id}`} className="p-2 hover:text-terracotta">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CampaignsPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [books, setBooks] = useState([]);
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({
    template: "newsletter",
    subject: "",
    intro: "",
    book_id: "",
    discount_code: "",
    body_html: "",
    segment_by_category: true,
  });
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get("/admin/campaigns"),
      api.get("/books"),
      api.get("/admin/discount-codes"),
    ]).then(([c, b, d]) => {
      setCampaigns(c.data); setBooks(b.data); setCodes(d.data);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject) { toast.error("Subject is required"); return; }
    if (form.template === "new_release" && !form.book_id) { toast.error("Pick a book"); return; }
    if (form.template === "discount" && !form.discount_code) { toast.error("Pick a code"); return; }
    if (!window.confirm("Send this to all subscribers now?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/campaigns/send", form);
      const failedMsg = data.failed > 0 ? `, ${data.failed} failed` : "";
      toast.success(`Sent to ${data.sent} subscriber${data.sent === 1 ? "" : "s"}${failedMsg}`);
      setForm({ template: "newsletter", subject: "", intro: "", book_id: "", discount_code: "", body_html: "", segment_by_category: true });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-campaigns" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={submit} className="lg:col-span-2 bg-white border border-edge p-6 space-y-4">
        <div className="flex items-center gap-2 font-serif text-2xl"><Send className="w-5 h-5 text-terracotta" />New campaign</div>

        <div>
          <label className="label-eyebrow block mb-1">Template</label>
          <Select value={form.template} onValueChange={(v) => setForm({ ...form, template: v })}>
            <SelectTrigger data-testid="campaign-template" className="rounded-none border-edge"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newsletter">Newsletter (free-form)</SelectItem>
              <SelectItem value="new_release">New release announcement</SelectItem>
              <SelectItem value="discount">Discount offer</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div><label className="label-eyebrow block mb-1">Subject line</label>
          <Input data-testid="campaign-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-none border-edge" /></div>

        {form.template !== "newsletter" && (
          <div><label className="label-eyebrow block mb-1">Intro message (optional)</label>
            <Textarea data-testid="campaign-intro" rows={3} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} className="rounded-none border-edge" placeholder="A short personal note before the main content…" /></div>
        )}

        {form.template === "new_release" && (
          <div>
            <label className="label-eyebrow block mb-1">Featured book</label>
            <Select value={form.book_id} onValueChange={(v) => setForm({ ...form, book_id: v })}>
              <SelectTrigger data-testid="campaign-book" className="rounded-none border-edge"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                {books.map((b) => <SelectItem key={b.id} value={b.id}>{b.title} — {b.author}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="mt-3 flex items-center gap-2 text-xs uppercase tracking-widest text-muted_ink cursor-pointer">
              <input
                data-testid="campaign-segment"
                type="checkbox"
                checked={form.segment_by_category}
                onChange={(e) => setForm({ ...form, segment_by_category: e.target.checked })}
                className="accent-terracotta"
              />
              Only email subscribers who like this book's category
            </label>
            <div className="text-[11px] text-muted_ink mt-1">
              Subscribers with no favorite categories still receive it.
            </div>
          </div>
        )}

        {form.template === "discount" && (
          <div>
            <label className="label-eyebrow block mb-1">Discount code</label>
            <Select value={form.discount_code} onValueChange={(v) => setForm({ ...form, discount_code: v })}>
              <SelectTrigger data-testid="campaign-code" className="rounded-none border-edge"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                {codes.filter((c) => c.active).map((c) => (
                  <SelectItem key={c.id} value={c.code}>{c.code} ({c.percent}% off)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {form.template === "newsletter" && (
          <div>
            <label className="label-eyebrow block mb-1">Body (HTML allowed)</label>
            <Textarea data-testid="campaign-body" rows={10} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} className="rounded-none border-edge font-mono text-sm" placeholder="<p>Hello reader…</p>" />
          </div>
        )}

        <button data-testid="campaign-send-btn" type="submit" disabled={sending} className="btn-primary w-full disabled:opacity-60">
          {sending ? "Sending…" : "Send to all subscribers"}
        </button>
      </form>

      <div className="lg:col-span-1">
        <div className="label-eyebrow text-sage mb-3 flex items-center gap-2"><Users className="w-3.5 h-3.5" />Sent history</div>
        <div className="space-y-2">
          {campaigns.length === 0 && <div className="text-muted_ink text-sm">No campaigns sent yet.</div>}
          {campaigns.map((c) => (
            <div key={c.id} className="border border-edge bg-white p-3">
              <div className="font-serif text-base">{c.subject}</div>
              <div className="text-xs text-muted_ink mt-1">
                {c.template} · {c.sent_count} sent{c.failed_count ? ` · ${c.failed_count} failed` : ""} · {new Date(c.sent_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MarketingTabs() {
  return (
    <Tabs defaultValue="subscribers" data-testid="marketing-tabs">
      <TabsList className="bg-transparent gap-2 p-0">
        <TabsTrigger value="subscribers" data-testid="tab-subscribers" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-paper">
          <Mail className="w-3.5 h-3.5 mr-2" />Subscribers
        </TabsTrigger>
        <TabsTrigger value="codes" data-testid="tab-codes" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-paper">
          <Tag className="w-3.5 h-3.5 mr-2" />Discount codes
        </TabsTrigger>
        <TabsTrigger value="campaigns" data-testid="tab-campaigns" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-paper">
          <Send className="w-3.5 h-3.5 mr-2" />Campaigns
        </TabsTrigger>
        <TabsTrigger value="feedback" data-testid="tab-feedback" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-paper">
          <MessageSquareHeart className="w-3.5 h-3.5 mr-2" />Feedback
        </TabsTrigger>
        <TabsTrigger value="nudges" data-testid="tab-nudges" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-paper">
          <Bell className="w-3.5 h-3.5 mr-2" />Nudges
        </TabsTrigger>
      </TabsList>
      <TabsContent value="subscribers" className="mt-6"><SubscribersPanel /></TabsContent>
      <TabsContent value="codes" className="mt-6"><DiscountCodesPanel /></TabsContent>
      <TabsContent value="campaigns" className="mt-6"><CampaignsPanel /></TabsContent>
      <TabsContent value="feedback" className="mt-6"><FeedbackPanel /></TabsContent>
      <TabsContent value="nudges" className="mt-6"><NudgesPanel /></TabsContent>
    </Tabs>
  );
}
